import React, { useState, useEffect, useMemo, useCallback, useRef, createRef, useContext } from 'react';
import { Grid, Stack, Box, Typography, CircularProgress } from '@mui/material';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Prompt } from 'react-router-dom';
import CitationPanel, { CitationProps } from './CitationPanel';
import Header from './Header';
import PreCutoffDisclaimer from './PreCutoffDisclaimer';
import {
  SummaryDataType,
  CitationItem,
  SummarySettings,
  ServerCitationItem,
  ServerSummaryData,
  ServerSummaryGenericContent,
} from './Interface';
import SettingsMenu from './SettingsMenu';
import SummaryItem from './SummaryItem';
import { SummarySettingsLabel, SummaryTypeIconMap, SummaryTypeTitleMap } from './Constants';
import { useQuery } from '@apollo/client';
import { AI_SUMMARY_QUERY, USER_ROLE_QUERY, AI_LETTER_QUERY } from '../graphql';
import { useRouteMatch } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { GET_PATIENT_DIAGNOSIS_LIST } from 'op-pages/RO/Careplan/DiagnosisPages/Queries';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import dayjs from 'dayjs';
import { NavigationContext } from 'op-contexts/index';
import { getDocumentTypeIcon } from '../SummaryUtils';
import { usePreventUnload } from 'shared-components/utils/CustomHooks';
import { logDatadogRumCustomAction } from 'op-utils/datadogRumCustomAction';

const SummaryTypeToSettingsKey = {
  overview: 'showOverview',
  cancerHistory: 'showCancerHistory',
  pastMedicalHistory: 'showPastMedicalHistory',
  medicationsAndAllergies: 'showMedicationsAndAllergies',
  familyHistory: 'showFamilyHistory',
  socialHistory: 'showSocialHistory',
} as const;

// `overview` intentionally omitted - LAI is not yet emitting overview data, so
// the section would always render the empty state. Tracked separately; re-add
// `overview: 'overview'` once LAI ingestion includes it.
const SummaryTypeMap = {
  cancerHistory: 'cancerHistory',
  pastMedicalHistory: 'medicalHistory',
  medicationsAndAllergies: 'medications',
  familyHistory: 'familyHistory',
  socialHistory: 'socialHistory',
} as const;

const SummaryContent = ({
  data,
  summarySettings,
  standardOrCompact,
  onRelatedItemClick,
  onCitationGroupSelection,
  selectedCitationGroupId,
  traceId,
  spanId,
  userRoleData,
  patientId,
}: {
  data: SummaryDataType[];
  summarySettings: SummarySettings;
  standardOrCompact: string;
  onRelatedItemClick: (content: CitationItem) => void;
  onCitationGroupSelection: (selectedGroupId: number | null) => void;
  selectedCitationGroupId: number | null;
  traceId?: string;
  spanId?: string;
  userRoleData?: {
    user?: {
      id: string;
      isSuperuser: boolean;
    };
  };
  patientId?: string;
}): JSX.Element => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const filteredSummaryData = useMemo(
    () =>
      data.filter((item) => {
        const isEnabledInSettings =
          summarySettings[
            SummaryTypeToSettingsKey[item.summaryType as keyof typeof SummaryTypeToSettingsKey] as keyof SummarySettings
          ];

        return isEnabledInSettings;
      }),
    [data, summarySettings],
  );

  // Scroll to top when switching between compact and standard
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [standardOrCompact]);

  const animationDuration = filteredSummaryData.length === 1 ? 1000 : 6000;
  return (
    <Box
      sx={{ flex: 1, minHeight: 0, overflow: 'hidden', paddingRight: '0px !important', marginRight: '0px !important' }}>
      <Box
        ref={scrollContainerRef}
        sx={{
          height: '100%',
          overflowY: 'auto',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
        }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: '786px',
            minHeight: '100%',
            '& .summary-enter': {
              opacity: 1,
              maxHeight: '0',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 3,
              backgroundColor: 'white',
            },
            '& .summary-enter-active': {
              opacity: 1,
              maxHeight: '4000px',
              overflow: 'hidden',
              transitionProperty: 'max-height',
              transitionDuration: '8000ms',
              transitionTimingFunction: 'cubic-bezier(0, 0, 1, 1)',
              backgroundColor: 'white',
            },
            '& .summary-exit': {
              opacity: 0.3,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              pointerEvents: 'none',
              zIndex: 1,
              clipPath: 'inset(0 0 0 0)',
            },
            '& .summary-exit-active': {
              opacity: 0.3,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              pointerEvents: 'none',
              zIndex: 1,
              clipPath: 'inset(0 0 0 0)',
              transitionProperty: 'clip-path',
              transitionDuration: '8000ms',
              transitionTimingFunction: 'cubic-bezier(100%, 0, 1, 1)',
            },
          }}>
          <TransitionGroup component={null}>
            <CSSTransition
              key={`${standardOrCompact}`}
              timeout={animationDuration}
              classNames="summary"
              onEnter={() => {
                setIsTransitioning(true);
                // Scroll to top before animation starts
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollTop = 0;
                }
              }}
              onEntered={() => setIsTransitioning(false)}
              onExit={() => setIsTransitioning(true)}
              onExited={() => setIsTransitioning(false)}>
              <Stack
                direction="column"
                sx={{
                  py: 2,
                  padding: '0px 16px',
                  width: '100%',
                  position: 'relative',
                }}>
                {filteredSummaryData.map((item) => (
                  <SummaryItem
                    key={item.summaryType}
                    title={SummaryTypeTitleMap[item.summaryType as keyof typeof SummaryTypeTitleMap]}
                    summaryType={item.summaryType as keyof typeof SummaryTypeIconMap}
                    contentType={standardOrCompact as 'standard' | 'compact'}
                    contents={standardOrCompact === 'standard' ? item.standardContent : item.compactContent}
                    onRelatedItemClick={onRelatedItemClick}
                    onCitationGroupSelection={onCitationGroupSelection}
                    selectedCitationGroupId={selectedCitationGroupId}
                    traceId={traceId}
                    spanId={spanId}
                    userRoleData={userRoleData}
                    patientId={patientId}
                  />
                ))}
                {isTransitioning && (
                  <Box
                    className="summary-transition-box"
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: '150px',
                      pointerEvents: 'none',
                      background: 'linear-gradient(to top, white, rgba(255, 255, 255, 0.9),  transparent)',
                      zIndex: 10,
                    }}
                  />
                )}
              </Stack>
            </CSSTransition>
          </TransitionGroup>
        </Box>
      </Box>
    </Box>
  );
};

const generateDataRelatedItemIds = (data: ServerSummaryData): SummaryDataType[] => {
  let relatedId = 0;
  let citationGroupId = 0;
  if (!data || Object.keys(data).length === 0) {
    return [];
  }
  return Object.keys(SummaryTypeMap).map((summaryType) => {
    const summaryTypeValue = SummaryTypeMap[summaryType as keyof typeof SummaryTypeMap];
    return {
      summaryType: summaryType,
      standardContent: data[summaryTypeValue]?.standard?.map((content: ServerSummaryGenericContent) => ({
        id: citationGroupId++,
        text: content.text,
        citations: content.citations.map((relatedItem: ServerCitationItem) => {
          relatedId++;
          return {
            title: relatedItem.title || relatedItem.resourceName || relatedItem.resourceId,
            documentUrl: relatedItem.resourceId,
            documentType: relatedItem.resourceType,
            icon: getDocumentTypeIcon(relatedItem.resourceType),
            relatedItemId: relatedId,
            highlightGeoms: relatedItem?.highlightGeoms,
            citedText: relatedItem?.citedText,
            author: relatedItem?.authorName,
            status: relatedItem?.status,
            dateOfDocument: (() => {
              if (!relatedItem.dateOfDocument || relatedItem.dateOfDocument.trim() === '') {
                return 'No Document Date';
              }

              const parsedDate = dayjs(relatedItem.dateOfDocument);
              const isValid = parsedDate.isValid();
              const formattedDate = isValid
                ? parsedDate.format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat)
                : 'No Document Date';
              return formattedDate;
            })(),
          };
        }),
        subItems: content.subItems?.map((subItem: ServerSummaryGenericContent) => ({
          id: citationGroupId++,
          text: subItem.text,
          citations: subItem.citations.map((relatedItem: ServerCitationItem) => {
            relatedId++;
            return {
              relatedItemId: relatedId,
              highlightGeoms: relatedItem?.highlightGeoms,
              documentUrl: relatedItem.resourceId,
              documentType: relatedItem.resourceType,
              icon: getDocumentTypeIcon(relatedItem.resourceType),
              dateOfDocument: dayjs(relatedItem.dateOfDocument).format(
                CurrentAppConfig.PatientCard.defaultAlternateDateFormat,
              ),
              title: relatedItem.title || relatedItem.resourceName || relatedItem.resourceId,
              citedText: relatedItem?.citedText,
              author: relatedItem?.authorName,
              status: relatedItem?.status,
            };
          }),
        })),
      })),
      compactContent: data[summaryTypeValue]?.compact?.map((content: ServerSummaryGenericContent) => ({
        text: content.text,
        id: citationGroupId++,
        citations: content.citations.map((relatedItem: ServerCitationItem) => {
          relatedId++;
          return {
            title: relatedItem.title || relatedItem.resourceName || relatedItem.resourceId,
            documentUrl: relatedItem.resourceId,
            documentType: relatedItem.resourceType,
            icon: getDocumentTypeIcon(relatedItem.resourceType),
            relatedItemId: relatedId,
            highlightGeoms: relatedItem?.highlightGeoms,
            citedText: relatedItem?.citedText,
            author: relatedItem?.authorName,
            status: relatedItem?.status,
            dateOfDocument: (() => {
              if (!relatedItem.dateOfDocument || relatedItem.dateOfDocument.trim() === '') {
                return 'No Document Date';
              }
              const parsedDate = dayjs(relatedItem.dateOfDocument);
              const isValid = parsedDate.isValid();
              const formattedDate = isValid
                ? parsedDate.format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat)
                : 'No Document Date';
              return formattedDate;
            })(),
          };
        }),
        subItems: content.subItems?.map((subItem: ServerSummaryGenericContent) => ({
          text: subItem.text,
          id: citationGroupId++,
          citations: subItem.citations.map((relatedItem: ServerCitationItem) => {
            relatedId++;
            return {
              relatedItemId: relatedId,
              highlightGeoms: relatedItem?.highlightGeoms,
              documentUrl: relatedItem.resourceId,
              documentType: relatedItem.resourceType,
              icon: getDocumentTypeIcon(relatedItem.resourceType),
              title: relatedItem.title || relatedItem.resourceName || relatedItem.resourceId,
              citedText: relatedItem?.citedText,
              author: relatedItem?.authorName,
              status: relatedItem?.status,
              dateOfDocument: dayjs(relatedItem.dateOfDocument).format(
                CurrentAppConfig.PatientCard.defaultAlternateDateFormat,
              ),
            };
          }),
        })),
      })),
    };
  });
};

const Summary = (): JSX.Element => {
  const {
    params: { id: patientId },
  } = useRouteMatch<{ id: string }>();
  const { data: aiSummaryData, loading: aiSummaryLoading } = useQuery(AI_SUMMARY_QUERY, {
    variables: {
      patientId: patientId,
    },
    skip: !patientId,
  });
  const { data: userRoleData } = useQuery(USER_ROLE_QUERY);

  const formatLastUpdated = (updatedAt: string | null): string | null => {
    if (!updatedAt) return null;
    const parsed = dayjs(updatedAt);
    if (!parsed.isValid()) return null;
    return parsed.format(CurrentAppConfig.AISummary.lastUpdatedDateTimeFormat);
  };
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCitationGroupId, setSelectedCitationGroupId] = useState<number | null>(null);
  const [summaryType, setSummaryType] = useState('compact');
  const [citationContent, setCitationContent] = useState<CitationProps | null>(null);
  const [summarySettings, setSummarySettings] = useState<SummarySettings>({
    showOverview: true,
    showCancerHistory: true,
    showPastMedicalHistory: true,
    showMedicationsAndAllergies: true,
    showFamilyHistory: true,
    showSocialHistory: true,
  });
  const summarySettingsRef = useRef(summarySettings);
  summarySettingsRef.current = summarySettings;

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [data, setData] = useState<SummaryDataType[]>([]);
  // Side panel expand/collapse state
  const { isSideNavbarCollapsed, setIsSideNavbarCollapsed } = useContext(NavigationContext);

  // Check if HPI document has been submitted
  const parsedPatientId = patientId ? parseInt(patientId, 10) : null;
  const isLetterOpen = citationContent?.documentType === 'letter';

  // Query for HPI document - only when letter panel is open
  const { data: aiLetterData } = useQuery(AI_LETTER_QUERY, {
    variables: {
      patientId: parsedPatientId,
      conversationId: isLetterOpen ? citationContent?.conversationId || null : null,
      letterType: 'hpi_document',
    },
    skip: !parsedPatientId || !isLetterOpen,
  });

  // Check if latest version has been submitted to EMR
  // Show warning only when letter is open and hasn't been submitted (emr_document_id is empty)
  const latestVersionNotSubmitted =
    isLetterOpen &&
    aiLetterData?.aiLetter &&
    (!aiLetterData.aiLetter.emrDocumentId || aiLetterData.aiLetter.emrDocumentId === '');

  // Prevent unload if HPI document is open and latest version hasn't been submitted
  usePreventUnload(latestVersionNotSubmitted || false);

  //Prefetch the primary diagnosis list for the patient
  const [getPrimaryDiagnosisList] = useLazyQuery(GET_PATIENT_DIAGNOSIS_LIST, {
    variables: { patientId, diagnosisType: 'primary' },
  });
  useEffect(() => {
    if (patientId) {
      getPrimaryDiagnosisList();
    }
  }, [patientId]);

  useEffect(() => {
    if (aiSummaryData) {
      setData(generateDataRelatedItemIds(aiSummaryData.aiSummary.summary));
      setIsLoading(false);
    }
  }, [aiSummaryData]);

  const handleSettingsChange = useCallback(
    (nextSettings: SummarySettings) => {
      const prev = summarySettingsRef.current;
      const changedKeys = (Object.keys(nextSettings) as (keyof SummarySettings)[]).filter(
        (key) => prev[key] !== nextSettings[key],
      );

      setSummarySettings(nextSettings);

      changedKeys.forEach((key) => {
        logDatadogRumCustomAction(nextSettings[key] ? 'ai_summary_setting_enable' : 'ai_summary_setting_disable', {
          patientId,
          viewMode: summaryType as 'standard' | 'compact',
          key: String(key),
        });
      });
    },
    [patientId, summaryType],
  );

  const handleStandard = () => {
    logDatadogRumCustomAction('ai_summary_view_standard', {
      patientId,
      viewMode: 'standard',
    });
    setSummaryType('standard');
  };

  const handleCompact = () => {
    logDatadogRumCustomAction('ai_summary_view_compact', {
      patientId,
      viewMode: 'compact',
    });
    setSummaryType('compact');
  };

  const handleCitationContent = (selectedRelatedItem: CitationItem | null) => {
    if (selectedRelatedItem) {
      logDatadogRumCustomAction('ai_summary_citation_open', {
        patientId,
        viewMode: summaryType as 'standard' | 'compact',
        documentType: selectedRelatedItem.documentType,
      });
      setIsSideNavbarCollapsed(true);
    } else {
      setIsSideNavbarCollapsed(false);
    }
    setCitationContent(selectedRelatedItem as CitationProps);
    setData((prevData) =>
      prevData.map((item) => ({
        ...item,
        standardContent: item.standardContent?.map((content) => ({
          ...content,
          citations: content.citations?.map((relatedItem) => {
            return { ...relatedItem, selected: relatedItem.relatedItemId === selectedRelatedItem?.relatedItemId };
          }),
          subItems: content.subItems?.map((subItem) => ({
            ...subItem,
            citations: subItem.citations?.map((relatedItem) => {
              return { ...relatedItem, selected: relatedItem.relatedItemId === selectedRelatedItem?.relatedItemId };
            }),
          })),
        })),
        compactContent: item.compactContent?.map((content) => ({
          ...content,
          citations: content.citations?.map((relatedItem) => {
            return { ...relatedItem, selected: relatedItem.relatedItemId === selectedRelatedItem?.relatedItemId };
          }),
          subItems: content.subItems?.map((subItem) => ({
            ...subItem,
            citations: subItem.citations?.map((relatedItem) => {
              return { ...relatedItem, selected: relatedItem.relatedItemId === selectedRelatedItem?.relatedItemId };
            }),
          })),
        })),
      })),
    );
  };

  return (
    <>
      <Prompt
        when={latestVersionNotSubmitted || false}
        message="This HPI document has NOT been submitted to the EMR. Are you sure you want to leave this page without submitting?"
      />
      <Grid container sx={{ height: '100%', overflow: 'hidden' }}>
        <Grid
          item
          xs={citationContent ? 6 : 12}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'all .5s ease-in-out' }}>
          <Stack sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Header
              title="Summary"
              onCompact={handleCompact}
              onStandard={handleStandard}
              compactOrStandard={summaryType}
              settingsMenu={
                <SettingsMenu
                  anchorEl={menuAnchorEl}
                  handleMenuClose={() => {
                    setMenuAnchorEl(null);
                  }}
                  settings={summarySettings}
                  setSettings={handleSettingsChange}
                  settingsLabel={SummarySettingsLabel}
                />
              }
              setMenuAnchorEl={setMenuAnchorEl}
            />
            <PreCutoffDisclaimer patientId={patientId} />
            {isLoading || aiSummaryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : data.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {aiSummaryData?.aiSummary?.updatedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 2, paddingBottom: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Last updated: {formatLastUpdated(aiSummaryData.aiSummary.updatedAt)}
                    </Typography>
                  </Box>
                )}
                <SummaryContent
                  data={data}
                  summarySettings={summarySettings}
                  standardOrCompact={summaryType}
                  onRelatedItemClick={handleCitationContent}
                  onCitationGroupSelection={setSelectedCitationGroupId}
                  selectedCitationGroupId={selectedCitationGroupId}
                  traceId={aiSummaryData.aiSummary.traceId}
                  spanId={aiSummaryData.aiSummary.spanId}
                  userRoleData={userRoleData}
                  patientId={patientId}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  padding: '32px',
                }}>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                  No available information.
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>
        <Grid item xs={citationContent ? 6 : 0}>
          <CitationPanel
            onClose={() => {
              handleCitationContent(null);
            }}
            citation={{
              ...citationContent!,
              emrDocumentId:
                citationContent?.documentType === 'letter'
                  ? aiLetterData?.aiLetter?.emrDocumentId
                  : citationContent?.emrDocumentId,
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
export default Summary;
